import React, { useState, useEffect } from 'react';
import { useOnlineStatus } from '../../hooks';

/**
 * OfflineIndicator component
 * 
 * Displays a visual indicator when the application is offline.
 * Uses browser's online/offline events to detect connection status.
 * Shows a "recently connected" message briefly when coming back online.
 */
const OfflineIndicator: React.FC = () => {
  const { isOnline, wasOffline, lastOnlineAt } = useOnlineStatus();
  const [showReconnected, setShowReconnected] = useState<boolean>(false);
  
  // Show "Reconnected" message for 5 seconds after coming back online
  useEffect(() => {
    if (isOnline && wasOffline && lastOnlineAt) {
      setShowReconnected(true);
      
      const timer = setTimeout(() => {
        setShowReconnected(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline, lastOnlineAt]);

  // Don't render anything if we're online and not showing the reconnected message
  if (isOnline && !showReconnected) {
    return null;
  }

  // Render offline indicator or reconnected message
  return (
    <div className={`fixed bottom-4 left-4 z-50 flex items-center gap-2 rounded-full px-3 py-1.5 text-white shadow-lg transition-all duration-300 ${
      !isOnline 
        ? 'bg-red-500' 
        : 'bg-green-500'
    }`}>
      <div className={`h-2 w-2 rounded-full ${
        !isOnline 
          ? 'bg-white animate-pulse' 
          : 'bg-white'
      }`}></div>
      
      <span className="text-sm font-medium">
        {!isOnline 
          ? "You're offline - changes will sync when reconnected" 
          : "Back online - syncing changes..."}
      </span>
    </div>
  );
};

export default OfflineIndicator;